import { useQuery } from "@tanstack/react-query";
import {
  BestTicketBlockDetails,
  HoldSeasonTicketSeatsResponse,
  PlanPackagePromotionDetails,
  PlanPackageUserFieldDetails,
  SectionDetails,
  TdcPackageDetails,
} from "features/types";
import { get, patch, post, put } from "services/http";
import planPackageServices from "services/planPackage.services";

export interface UpdatePlanPackageUserFieldsRequest {
  planPackageId: number;
  fields: Array<{ fieldId: number; required: boolean }>;
}

export const fetchPlanPackageAvailableSections = (
  planPackageId: number,
  params?: { tdcPackageListId?: string; packageId?: string }
) => {
  return get({
    url: `plan_packages/${planPackageId}/available_sections`,
    params:
      params?.tdcPackageListId && params?.packageId
        ? {
            tdcPackageListId: params.tdcPackageListId,
            packageId: params.packageId,
          }
        : undefined,
  }) as Promise<SectionDetails[]>;
};

export const fetchPlanPackageSectionAvailableBlocks = (
  planPackageId: number,
  section: string,
  eventType?: string,
  tdcPackageListId?: string,
  packageId?: string
) => {
  return get({
    url: `plan_packages/season_ticket/available_blocks`,
    params: {
      planPackageId,
      section,
      eventType,
      tdcPackageListId,
      packageId,
    },
  }) as Promise<BestTicketBlockDetails[]>;
};

export const holdSeasonTicketPurchaseSeats = (params: {
  planPackageId: number;
  section: string;
  seatIds: number[];
  eventType?: string;
  tdcPackageListId?: string;
  tdcPackageBuyerTypeId?: string;
  tdcPackageId?: string;
}) => {
  return patch({
    url: `tickets/hold_season_ticket_seats`,
    params: {
      planPackageId: params.planPackageId,
      section: params.section,
      seatIds: params.seatIds,
      eventType: params.eventType,
      tdcPackageListId: params.tdcPackageListId,
      tdcPackageBuyerTypeId: params.tdcPackageBuyerTypeId,
      tdcPackageId: params.tdcPackageId,
    },
  }) as Promise<HoldSeasonTicketSeatsResponse>;
};

export const useFetchPlanPackagesQuery = () => {
  return useQuery(["fetchPlanPackages"], () => planPackageServices.find(), {
    staleTime: Infinity,
  });
};

export const useFetchPlanPackageAvailableSectionsQuery = (
  planPackageId: number | undefined,
  tdcPackageListId?: string,
  packageId?: string
) => {
  return useQuery(
    [
      "fetchPlanPackageAvailableSections",
      planPackageId,
      tdcPackageListId,
      packageId,
    ],
    () =>
      fetchPlanPackageAvailableSections(planPackageId!, {
        tdcPackageListId,
        packageId,
      }),
    {
      staleTime: 0,
      enabled: !!planPackageId,
    }
  );
};

export const useFetchPlanPackageSectionAvailableBlocksQuery = (
  planPackageId: number | undefined,
  section: string | undefined,
  eventType?: string,
  tdcPackageListId?: string,
  packageId?: string
) => {
  return useQuery(
    [
      "fetchPlanPackageSectionAvailableBlocks",
      planPackageId,
      section,
      eventType,
      tdcPackageListId,
      packageId,
    ],
    () =>
      fetchPlanPackageSectionAvailableBlocks(
        planPackageId!,
        section!,
        eventType,
        tdcPackageListId,
        packageId
      ),
    {
      staleTime: 0,
      enabled: !!planPackageId && !!section,
    }
  );
};

interface HoldSeasonTicketSeatsRequest {
  planPackageId: number;
  section: string;
  seatIds: number[];
  eventType?: string;
  tdcPackageListId?: string;
  tdcPackageBuyerTypeId?: string;
  tdcPackageId?: string;
}

export const holdSeasonTicketSeatsRequest = (
  params: HoldSeasonTicketSeatsRequest
) => {
  return post({
    url: `tickets/hold_season_ticket_seats`,
    params: {
      planPackageId: params.planPackageId,
      section: params.section,
      seatIds: params.seatIds,
      eventType: params.eventType,
      tdcPackageListId: params.tdcPackageListId,
      tdcPackageBuyerTypeId: params.tdcPackageBuyerTypeId,
      tdcPackageId: params.tdcPackageId,
    },
  }) as Promise<HoldSeasonTicketSeatsResponse>;
};

export const useFetchPlanPackageUserFieldsQuery = (
  planPackageId: number | undefined
) => {
  return useQuery(
    ["fetchPlanPackageUserFields", planPackageId],
    () => {
      return get({
        url: `/plan_packages/fields/${planPackageId}`,
      }) as Promise<Array<PlanPackageUserFieldDetails>>;
    },
    {
      staleTime: 0,
      enabled: !!planPackageId,
    }
  );
};

export const updatePlanPackageUserFieldsFetcher = (
  params: UpdatePlanPackageUserFieldsRequest
) => {
  return put({
    url: `/plan_packages/fields/${params.planPackageId}`,
    params: params.fields,
  });
};

export const useFetchPlanPackagePromotionsQuery = () => {
  return useQuery(
    ["fetchPlanPackagePromotions"],
    () => {
      return get({
        url: "/plan_packages/promotions/list/active",
      }) as Promise<Array<PlanPackagePromotionDetails>>;
    },
    {
      staleTime: Infinity,
    }
  );
};

export const useFetchTdcPackagesQuery = () => {
  return useQuery(
    ["fetchTdcPackages"],
    () => {
      return get({
        url: "/plan_packages/tdc_packages",
      }) as Promise<Array<TdcPackageDetails>>;
    },
    {
      staleTime: 0,
    }
  );
};

export const useFetchTdcPackageListAvailableSectionsQuery = (
  planPackageId: number | undefined,
  tdcPackageListId: string | undefined,
  packageId: string | undefined
) => {
  return useQuery(
    [
      "fetchTdcPackageListAvailableSections",
      planPackageId,
      tdcPackageListId,
      packageId,
    ],
    () => {
      return fetchPlanPackageAvailableSections(planPackageId!, {
        tdcPackageListId,
        packageId,
      });
    },
    {
      staleTime: 0,
      enabled: !!planPackageId && !!tdcPackageListId && !!packageId,
    }
  );
};

// TDC seat availability is now served via `plan_packages/season_ticket/available_blocks`.
